/**
 * The Android signing of a service, for release builds made by Flow (PUT /projects/:id/device-signing/:service): the
 * keystore file (.jks / .keystore, at most 64 KB, read here as base64), its password, the key alias and the key's
 * password. Nothing comes back: once saved only the alias and when it was set show.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Input } from 'antd';
import { saveDeviceSigning } from '../../services/DeviceBuildsService';

export const MAX_KEYSTORE_BYTES = 64 * 1024;

/** The file's bytes as base64 (FileReader's data URL without its prefix). */
export function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error || new Error('The file could not be read.'));
    reader.readAsDataURL(file);
  });
}

export default function SigningForm({ projectId, service, onSaved, onCancel = null }) {
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [storePassword, setStorePassword] = useState('');
  const [keyAlias, setKeyAlias] = useState('');
  const [keyPassword, setKeyPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const pick = (event) => {
    const next = event.target.files?.[0] || null;
    setFileError(null);
    if (next && next.size > MAX_KEYSTORE_BYTES) {
      setFile(null);
      setFileError('This file is bigger than 64 KB: a keystore is usually a few KB. Pick the .jks or .keystore file.');
      return;
    }
    setFile(next);
  };

  const ready = file && storePassword && keyAlias.trim() && keyPassword;
  const save = async() => {
    setSaving(true);
    setError(null);
    try {
      const keystore = await readAsBase64(file);
      const answer = await saveDeviceSigning(projectId, service, { keystore, storePassword, keyAlias: keyAlias.trim(), keyPassword });
      setStorePassword('');
      setKeyPassword('');
      onSaved(answer);
    } catch (err) {
      setError(err?.message || 'The keystore could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="devb-panel" data-testid="devb-signing-form">
      <h4>Sign the Android app</h4>
      <p className="devb-muted">
        A release for Google Play is signed with your upload key. The keystore is kept in Flow&apos;s secret store and
        never shown again: keep your own copy, every update needs the same key.
      </p>
      <label className="devb-field">
        <span>Keystore file (.jks or .keystore)</span>
        <input accept=".jks,.keystore,application/octet-stream" data-testid="devb-signing-file" onChange={pick} type="file" />
      </label>
      {fileError && <p className="devb-bad" data-testid="devb-signing-file-error">{fileError}</p>}
      <label className="devb-field">
        <span>Keystore password</span>
        <Input.Password autoComplete="new-password" data-testid="devb-signing-store-password" onChange={(e) => setStorePassword(e.target.value)} value={storePassword} />
      </label>
      <label className="devb-field">
        <span>Key alias</span>
        <Input autoComplete="off" data-testid="devb-signing-alias" onChange={(e) => setKeyAlias(e.target.value)} placeholder="upload" value={keyAlias} />
      </label>
      <label className="devb-field">
        <span>Key password</span>
        <Input.Password autoComplete="new-password" data-testid="devb-signing-key-password" onChange={(e) => setKeyPassword(e.target.value)} value={keyPassword} />
      </label>
      {error && <Alert className="devb-alert" data-testid="devb-signing-error" showIcon title={error} type="error" />}
      <div className="devb-row devb-row-end">
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button data-testid="devb-signing-save" disabled={!ready} loading={saving} onClick={save} type="primary">Save keystore</Button>
      </div>
    </section>
  );
}

SigningForm.propTypes = {
  onCancel: PropTypes.func,
  // (signing) => after it was saved: GET device-signing's shape.
  onSaved: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  service: PropTypes.string.isRequired,
};
